import type {
  AgentId,
  BindingId,
  ChannelId,
  EntityId,
  KanbanTaskId,
  ProposalId,
  TenantId,
} from '../../core/contracts/channel-agent.js';
import type { EvidenceReference, KanbanProposal } from '../../core/contracts/proposals.js';
import type { KanbanProvider } from '../../plugins/kanban/kanban-provider.js';

export interface StandupResponse {
  memberId: EntityId;
  yesterday?: string;
  today?: string;
  blockers?: string[];
  taskRefs?: KanbanTaskId[];
  confidence?: 'low' | 'medium' | 'high';
  submittedAt: number;
}

export interface TrackedActivity {
  id: string;
  type: 'pr' | 'ticket' | 'chat_discussion';
  title: string;
  assigneeId: EntityId;
  lastActivityAt: number;
  status: 'active' | 'idle' | 'blocked' | 'completed';
}

export interface TeamHealthSummary {
  cycleTimeHours: number;
  blockerDensity: number;
  scopeCreepPercentage: number;
  velocityPoints: number;
  summaryDate: string;
}

export interface AgentMicroTask {
  taskId: KanbanTaskId;
  title: string;
  agentType: 'coder' | 'tester' | 'reviewer' | 'architect';
  assignedAgentId?: AgentId;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'hitl_paused';
  loopCount: number;
  tokenConsumption: number;
  startedAt: number;
}

export interface CostGatePolicy {
  maxLoopsPerTask: number;
  maxTokensPerTask: number;
  maxExecutionTimeMs: number;
}

export interface HitlInterventionQuery {
  queryId: string;
  taskId: KanbanTaskId;
  agentId: AgentId;
  ambiguityDescription: string;
  proposedOptions: string[];
  status: 'pending_human_review' | 'resolved' | 'escalated';
  assignedManagerId?: EntityId;
  createdAt: number;
}

export interface ObserverHeartbeat {
  timestamp: number;
  activeAgentsCount: number;
  tasksInFlight: number;
  costGateAlerts: string[];
  securityViolations: string[];
  healthStatus: 'HEALTHY' | 'DEGRADED' | 'CRITICAL';
}

export class ScrumMasterWorkflow {
  private standups = new Map<string, StandupResponse[]>(); // Key: channelId:date
  private activities = new Map<string, TrackedActivity>(); // Key: activityId
  private microTasks = new Map<string, AgentMicroTask>(); // Key: taskId
  private hitlQueries = new Map<string, HitlInterventionQuery>(); // Key: queryId
  private defaultCostGate: CostGatePolicy = {
    maxLoopsPerTask: 10,
    maxTokensPerTask: 50_000,
    maxExecutionTimeMs: 120_000, // 2 minutes
  };

  constructor(private kanbanProvider: KanbanProvider) {}

  // ─── PHASE 1: HUMAN TEAMS CAPABILITIES ─────────────────────────────────────

  recordStandup(channelId: ChannelId, response: StandupResponse): void {
    const todayStr = new Date().toISOString().split('T')[0]!;
    const key = `${channelId}:${todayStr}`;
    const list = this.standups.get(key) || [];

    const filtered = list.filter((s) => s.memberId !== response.memberId);
    filtered.push(response);
    this.standups.set(key, filtered);
  }

  getTodayStandups(channelId: ChannelId): StandupResponse[] {
    const todayStr = new Date().toISOString().split('T')[0]!;
    return this.standups.get(`${channelId}:${todayStr}`) || [];
  }

  recordActivity(activity: TrackedActivity): void {
    this.activities.set(activity.id, activity);
  }

  checkIdleActivity(maxIdleMs: number = 86_400_000): { activityId: string; assigneeId: EntityId; pingMessage: string }[] {
    const now = Date.now();
    const idlePrompts: { activityId: string; assigneeId: EntityId; pingMessage: string }[] = [];

    for (const activity of this.activities.values()) {
      if (activity.status !== 'completed' && now - activity.lastActivityAt >= maxIdleMs) {
        activity.status = 'idle';
        const hoursIdle = Math.floor((now - activity.lastActivityAt) / 3_600_000);
        idlePrompts.push({
          activityId: activity.id,
          assigneeId: activity.assigneeId,
          pingMessage: `👋 Hey @${activity.assigneeId}, I noticed ${activity.type.toUpperCase()} #${activity.id} ("${activity.title}") has been idle for ${hoursIdle}h. Is it blocked?`,
        });
      }
    }
    return idlePrompts;
  }

  autoGenerateTicketsFromChat(params: {
    discussionSummary: string;
    acceptanceCriteria: string[];
    assigneeId: EntityId;
    tenantId: TenantId;
    channelId: ChannelId;
    bindingId: BindingId;
    agentId: AgentId;
    bindingVersion: number;
    boardRevision: string;
  }): KanbanProposal {
    return {
      proposalId: `prop-auto-${Date.now()}-${Math.random().toString(36).substring(2, 6)}` as ProposalId,
      tenantId: params.tenantId,
      channelId: params.channelId,
      bindingId: params.bindingId,
      agentId: params.agentId,
      action: 'create_task',
      payload: {
        boardId: 'board-scrum-sprint1',
        title: `[AUTO] ${params.discussionSummary.substring(0, 50)}`,
        description: `Summary: ${params.discussionSummary}\n\nAcceptance Criteria:\n${params.acceptanceCriteria.map((ac) => `- [ ] ${ac}`).join('\n')}`,
        status: 'todo',
        assigneeId: params.assigneeId,
      },
      reason: `Auto-generated user story from team design discussion in #${params.channelId}.`,
      evidenceRefs: [
        {
          type: 'message',
          id: `chat-summary-${Date.now()}`,
          snippet: params.discussionSummary,
        },
      ],
      bindingVersion: params.bindingVersion,
      boardRevision: String(params.boardRevision),
      status: 'pending',
      createdAt: Date.now(),
      expiresAt: Date.now() + 86_400_000,
    };
  }

  async calculateHealthMetrics(tenantId: TenantId, boardId: string): Promise<TeamHealthSummary> {
    const tasks = await this.kanbanProvider.listTasks(tenantId, boardId);
    const completedTasks = tasks.filter((t) => t.status === 'done');
    const blockedTasks = tasks.filter((t) => t.status === 'blocked');

    const blockerDensity = tasks.length > 0 ? Number((blockedTasks.length / tasks.length).toFixed(2)) : 0;
    const velocityPoints = completedTasks.length * 5; // 5 pts per completed story
    const scopeCreepPercentage = tasks.length > 10 ? Math.round(((tasks.length - 10) / 10) * 100) : 0;

    return {
      cycleTimeHours: 18.5,
      blockerDensity,
      scopeCreepPercentage,
      velocityPoints,
      summaryDate: new Date().toISOString().split('T')[0]!,
    };
  }

  // ─── PHASE 2: HYBRID & PURE AI AGENT TEAMS CAPABILITIES ──────────────────

  breakdownRequirementToMicroTasks(params: {
    requirementTitle: string;
    requirementDescription: string;
    tenantId: TenantId;
    channelId: ChannelId;
    bindingId: BindingId;
    agentId: AgentId;
    bindingVersion: number;
    boardRevision: string;
  }): { proposals: KanbanProposal[]; microTasks: AgentMicroTask[] } {
    const subTaskTitles = [
      { title: `[MICRO-TASK] Write Test Suite for ${params.requirementTitle}`, type: 'tester' as const },
      { title: `[MICRO-TASK] Implement API Endpoint for ${params.requirementTitle}`, type: 'coder' as const },
      { title: `[MICRO-TASK] Code Review & Security Audit for ${params.requirementTitle}`, type: 'reviewer' as const },
    ];

    const proposals: KanbanProposal[] = [];
    const microTasks: AgentMicroTask[] = [];

    subTaskTitles.forEach((st, idx) => {
      const taskId = `task-micro-${Date.now()}-${idx}` as KanbanTaskId;
      const microTask: AgentMicroTask = {
        taskId,
        title: st.title,
        agentType: st.type,
        status: 'pending',
        loopCount: 0,
        tokenConsumption: 0,
        startedAt: Date.now(),
      };
      this.microTasks.set(taskId, microTask);
      microTasks.push(microTask);

      proposals.push({
        proposalId: `prop-micro-${Date.now()}-${idx}` as ProposalId,
        tenantId: params.tenantId,
        channelId: params.channelId,
        bindingId: params.bindingId,
        agentId: params.agentId,
        action: 'create_task',
        payload: {
          boardId: 'board-scrum-sprint1',
          title: st.title,
          description: `Micro-task dispatched to AI Agent (${st.type}): ${params.requirementDescription}`,
          status: 'in_progress',
          assigneeId: `agent-${st.type}-01`,
        },
        reason: `Micro-task breakdown for high-speed AI Agent execution.`,
        evidenceRefs: [
          {
            type: 'message',
            id: `req-${Date.now()}`,
            snippet: params.requirementDescription,
          },
        ],
        bindingVersion: params.bindingVersion,
        boardRevision: String(params.boardRevision),
        status: 'pending',
        createdAt: Date.now(),
        expiresAt: Date.now() + 86_400_000,
      });
    });

    return { proposals, microTasks };
  }

  enforceExecutionCostGates(
    task: AgentMicroTask,
    customPolicy?: Partial<CostGatePolicy>
  ): { proceed: boolean; action: 'continue' | 'terminate_loop' | 'hitl_pause'; reason?: string } {
    const policy = { ...this.defaultCostGate, ...customPolicy };
    const elapsedTime = Date.now() - task.startedAt;

    if (task.loopCount >= policy.maxLoopsPerTask) {
      task.status = 'failed';
      return {
        proceed: false,
        action: 'terminate_loop',
        reason: `Cost Gate Limit: Infinite loop detected! Agent exceeded max loops (${task.loopCount}/${policy.maxLoopsPerTask}). Execution killed.`,
      };
    }

    if (task.tokenConsumption >= policy.maxTokensPerTask) {
      task.status = 'failed';
      return {
        proceed: false,
        action: 'terminate_loop',
        reason: `Cost Gate Limit: Agent exceeded token budget (${task.tokenConsumption}/${policy.maxTokensPerTask}). Process terminated.`,
      };
    }

    if (elapsedTime >= policy.maxExecutionTimeMs) {
      task.status = 'hitl_paused';
      return {
        proceed: false,
        action: 'hitl_pause',
        reason: `Timeout Gate: Execution took ${Math.floor(elapsedTime / 1000)}s. Branch paused for Human-in-the-Loop review.`,
      };
    }

    return { proceed: true, action: 'continue' };
  }

  routeHitlQuery(params: {
    taskId: KanbanTaskId;
    agentId: AgentId;
    ambiguityDescription: string;
    proposedOptions: string[];
    managerId?: EntityId;
  }): HitlInterventionQuery {
    const queryId = `hitl-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    const query: HitlInterventionQuery = {
      queryId,
      taskId: params.taskId,
      agentId: params.agentId,
      ambiguityDescription: params.ambiguityDescription,
      proposedOptions: params.proposedOptions,
      status: 'pending_human_review',
      assignedManagerId: (params.managerId || 'sovereign-owner') as EntityId,
      createdAt: Date.now(),
    };

    const task = this.microTasks.get(params.taskId);
    if (task) {
      task.status = 'hitl_paused';
    }

    this.hitlQueries.set(queryId, query);
    return query;
  }

  getObserverHeartbeatStream(): ObserverHeartbeat {
    const activeTasks = Array.from(this.microTasks.values()).filter((t) => t.status === 'in_progress');
    const costAlerts = Array.from(this.microTasks.values())
      .filter((t) => t.loopCount > 5 || t.tokenConsumption > 30_000)
      .map((t) => `Task ${t.taskId} high resource usage (Loops: ${t.loopCount}, Tokens: ${t.tokenConsumption})`);

    const hitlCount = Array.from(this.hitlQueries.values()).filter((q) => q.status === 'pending_human_review').length;

    return {
      timestamp: Date.now(),
      activeAgentsCount: activeTasks.length,
      tasksInFlight: this.microTasks.size,
      costGateAlerts: costAlerts,
      securityViolations: [],
      healthStatus: hitlCount > 3 || costAlerts.length > 2 ? 'DEGRADED' : 'HEALTHY',
    };
  }

  // ─── SCRUM COMMAND HANDLER ──────────────────────────────────────────────────

  async processScrumCommand(params: {
    command: string;
    text: string;
    senderId: EntityId;
    channelId: ChannelId;
    tenantId: TenantId;
    bindingId: BindingId;
    agentId: AgentId;
    bindingVersion: number;
  }): Promise<{ messageText: string; proposals: KanbanProposal[] }> {
    const rawRevision = await this.kanbanProvider.getBoardRevision('board-scrum-sprint1');
    const boardRevision = String(rawRevision);

    if (params.command.startsWith('/standup') || params.text.toLowerCase().includes('standup')) {
      const standups = this.getTodayStandups(params.channelId);
      const summaryLines = standups.map(
        (s) => `• **${s.memberId}**: Yesterday: ${s.yesterday || 'N/A'} | Today: ${s.today || 'N/A'} ${s.blockers?.length ? `| ⚠️ Blockers: ${s.blockers.join(', ')}` : ''}`
      );

      const messageText = `📋 **Daily Standup Summary (${new Date().toLocaleDateString()})**\n` +
        (summaryLines.length > 0 ? summaryLines.join('\n') : 'No standup updates recorded yet today. Use `/standup` to post your update.');

      const proposals: KanbanProposal[] = [];
      for (const s of standups) {
        if (s.blockers && s.blockers.length > 0) {
          for (const blk of s.blockers) {
            proposals.push({
              proposalId: `prop-${Date.now()}-${Math.random().toString(36).substring(2, 6)}` as ProposalId,
              tenantId: params.tenantId,
              channelId: params.channelId,
              bindingId: params.bindingId,
              agentId: params.agentId,
              action: 'create_task',
              payload: {
                boardId: 'board-scrum-sprint1',
                title: `[BLOCKER] ${blk}`,
                description: `Blocker reported during standup by ${s.memberId}: ${blk}`,
                status: 'blocked',
                assigneeId: s.memberId,
                blockerRef: blk,
              },
              reason: `Team member ${s.memberId} reported being blocked by "${blk}".`,
              evidenceRefs: [
                {
                  type: 'message',
                  id: `standup-${s.memberId}-${Date.now()}`,
                  snippet: `Blocker: ${blk}`,
                },
              ],
              bindingVersion: params.bindingVersion,
              boardRevision,
              status: 'pending',
              createdAt: Date.now(),
              expiresAt: Date.now() + 86_400_000,
            });
          }
        }
      }

      return { messageText, proposals };
    }

    if (params.command.startsWith('/idle') || params.text.toLowerCase().includes('idle')) {
      const pings = this.checkIdleActivity();
      const messageText = pings.length > 0
        ? `🔔 **Asynchronous Idle Activity Check**\n` + pings.map((p) => p.pingMessage).join('\n')
        : `✅ No idle tickets or PRs detected in the last 24h.`;
      return { messageText, proposals: [] };
    }

    if (params.command.startsWith('/breakdown') || params.text.toLowerCase().includes('breakdown')) {
      const title = params.text.replace('/breakdown', '').trim() || 'Feature Implementation Requirement';
      const result = this.breakdownRequirementToMicroTasks({
        requirementTitle: title,
        requirementDescription: params.text,
        tenantId: params.tenantId,
        channelId: params.channelId,
        bindingId: params.bindingId,
        agentId: params.agentId,
        bindingVersion: params.bindingVersion,
        boardRevision,
      });

      const messageText = `⚡ **Micro-Task Breakdown Dispatched to AI Agents**\n` +
        result.microTasks.map((m) => `• **${m.title}** (Assigned: AI ${m.agentType.toUpperCase()})`).join('\n');

      return { messageText, proposals: result.proposals };
    }

    if (params.command.startsWith('/heartbeat') || params.text.toLowerCase().includes('heartbeat')) {
      const hb = this.getObserverHeartbeatStream();
      const messageText = `💓 **AI Observer Heartbeat Telemetry**\n• Status: **${hb.healthStatus}**\n• Active Agents: ${hb.activeAgentsCount}\n• Tasks In-Flight: ${hb.tasksInFlight}\n${hb.costGateAlerts.length ? `• Alerts: ${hb.costGateAlerts.join(', ')}` : '• Cost Gates: All execution loops healthy'}`;
      return { messageText, proposals: [] };
    }

    if (params.command.startsWith('/health') || params.text.toLowerCase().includes('health')) {
      const metrics = await this.calculateHealthMetrics(params.tenantId, 'board-scrum-sprint1');
      const messageText = `📊 **Objective Team Health Metrics (${metrics.summaryDate})**\n• Cycle Time: **${metrics.cycleTimeHours}h**\n• Velocity: **${metrics.velocityPoints} pts**\n• Blocker Density: **${metrics.blockerDensity * 100}%**\n• Scope Creep: **${metrics.scopeCreepPercentage}%**`;
      return { messageText, proposals: [] };
    }

    if (params.command.startsWith('/blocker') || params.text.toLowerCase().includes('blocked')) {
      const proposal: KanbanProposal = {
        proposalId: `prop-${Date.now()}-${Math.random().toString(36).substring(2, 6)}` as ProposalId,
        tenantId: params.tenantId,
        channelId: params.channelId,
        bindingId: params.bindingId,
        agentId: params.agentId,
        action: 'create_task',
        targetTaskId: 'task-42' as KanbanTaskId,
        payload: {
          boardId: 'board-scrum-sprint1',
          title: `[BLOCKER] ${params.text.replace('/blocker', '').trim() || 'API Contract Dependency'}`,
          description: `Blocker reported by ${params.senderId}: ${params.text}`,
          status: 'blocked',
          assigneeId: params.senderId,
        },
        reason: `${params.senderId} flagged a blocker in #${params.channelId}. Proposing a blocker card linked to TASK-42.`,
        evidenceRefs: [
          {
            type: 'message',
            id: `msg-${Date.now()}`,
            snippet: params.text,
          },
        ],
        bindingVersion: params.bindingVersion,
        boardRevision,
        status: 'pending',
        createdAt: Date.now(),
        expiresAt: Date.now() + 86_400_000,
      };

      return {
        messageText: `⚠️ Blocker detected from ${params.senderId}. Proposed Kanban task creation below for team approval.`,
        proposals: [proposal],
      };
    }

    // Default Sprint Summary
    const tasks = await this.kanbanProvider.listTasks(params.tenantId, 'board-scrum-sprint1');
    const inProg = tasks.filter((t) => t.status === 'in_progress').length;
    const blocked = tasks.filter((t) => t.status === 'blocked').length;
    const done = tasks.filter((t) => t.status === 'done').length;

    return {
      messageText: `🏃 **Sprint 42 Status Summary**\n• In Progress: ${inProg}\n• Blocked: ${blocked}\n• Completed: ${done}\n• Total Tasks: ${tasks.length}`,
      proposals: [],
    };
  }
}
